import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Upload,
  Trash2,
  Copy,
  Check,
  LogOut,
  Loader2,
  ImageIcon,
  ShieldCheck,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const TOKEN_KEY = "josfit_admin_token";

const api = axios.create({ baseURL: API });
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onSuccess(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#070A12] px-4">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur"
      >
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-400 text-emerald-950">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <div>
            <h1 className="font-outfit text-xl font-extrabold text-white">Espace admin</h1>
            <p className="text-xs text-slate-400">Jos'FIT · Gestion des médias</p>
          </div>
        </div>
        <label className="mb-1 block text-xs font-semibold text-slate-400">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-email-input"
          className="mb-4 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white outline-none transition-colors focus:border-emerald-400"
          placeholder="contact@josfit.app"
        />
        <label className="mb-1 block text-xs font-semibold text-slate-400">Mot de passe</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          className="mb-2 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white outline-none transition-colors focus:border-emerald-400"
          placeholder="••••••••"
        />
        {error && (
          <p data-testid="admin-login-error" className="mb-2 text-sm text-rose-400">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-400 px-4 py-3 text-sm font-bold text-emerald-950 transition-colors hover:bg-emerald-300 disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
        </button>
      </form>
    </div>
  );
}

function ImageCard({ img, onDelete }) {
  const [copied, setCopied] = useState(false);
  const fullUrl = `${BACKEND}${img.url}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = fullUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("URL copiée dans le presse-papiers");
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div
      data-testid={`admin-image-card-${img.id}`}
      className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]"
    >
      <div className="aspect-video overflow-hidden bg-black/40">
        <img
          src={fullUrl}
          alt={img.original_filename}
          className="h-full w-full object-contain"
          loading="lazy"
        />
      </div>
      <div className="p-3">
        <p className="truncate text-sm font-semibold text-white" title={img.original_filename}>
          {img.original_filename}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">{(img.size / 1024).toFixed(1)} Ko</p>
        <div className="mt-3 flex gap-2">
          <button
            onClick={copy}
            data-testid={`admin-copy-url-${img.id}`}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-emerald-400/10 px-3 py-2 text-xs font-bold text-emerald-300 transition-colors hover:bg-emerald-400/20"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copié" : "Copier l'URL"}
          </button>
          <button
            onClick={() => onDelete(img)}
            data-testid={`admin-delete-${img.id}`}
            className="flex items-center justify-center rounded-lg bg-rose-500/10 px-3 py-2 text-rose-400 transition-colors hover:bg-rose-500/20"
            aria-label="Supprimer"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function BannerEditor() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/banner")
      .then(({ data }) =>
        setForm({
          enabled: Boolean(data.enabled),
          message: data.message || "",
          cta_label: data.cta_label || "",
          cta_url: data.cta_url || "",
          launch_date: data.launch_date || "",
        })
      )
      .catch(() => toast.error("Impossible de charger le bandeau"));
  }, []);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(
        "/admin/banner",
        { ...form, launch_date: form.launch_date || null },
        { headers: authHeader() }
      );
      toast.success("Bandeau d'annonce mis à jour");
    } catch {
      toast.error("Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full rounded-xl border border-white/10 bg-black/40 px-4 py-2.5 text-sm text-white outline-none transition-colors focus:border-emerald-400";

  return (
    <section
      data-testid="banner-editor"
      className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-6"
    >
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-outfit text-lg font-extrabold text-white">Bandeau d'annonce</h2>
        <button
          type="button"
          role="switch"
          aria-checked={form.enabled}
          onClick={() => set("enabled", !form.enabled)}
          data-testid="banner-enabled-toggle"
          className={`relative h-6 w-11 rounded-full transition-colors ${form.enabled ? "bg-emerald-400" : "bg-white/15"}`}
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${form.enabled ? "left-[22px]" : "left-0.5"}`}
          />
        </button>
      </div>
      <p className="mb-4 -mt-2 text-xs text-slate-500">
        {form.enabled ? "Le bandeau est affiché sur le site." : "Le bandeau est masqué."}
      </p>

      <label className="mb-1 block text-xs font-semibold text-slate-400">Message</label>
      <textarea
        rows={2}
        value={form.message}
        onChange={(e) => set("message", e.target.value)}
        data-testid="banner-message-input"
        className={`${field} mb-4 resize-none`}
        placeholder="Lancement officiel de Jos'FIT le 1er octobre…"
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-400">Texte du bouton</label>
          <input
            value={form.cta_label}
            onChange={(e) => set("cta_label", e.target.value)}
            data-testid="banner-cta-label-input"
            className={field}
            placeholder="Voir sur Google Play"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-400">Date de lancement (compte à rebours)</label>
          <input
            type="date"
            value={form.launch_date}
            onChange={(e) => set("launch_date", e.target.value)}
            data-testid="banner-launch-date-input"
            className={field}
          />
        </div>
      </div>

      <label className="mb-1 mt-4 block text-xs font-semibold text-slate-400">Lien du bouton</label>
      <input
        value={form.cta_url}
        onChange={(e) => set("cta_url", e.target.value)}
        data-testid="banner-cta-url-input"
        className={`${field} mb-5`}
        placeholder="https://play.google.com/store/apps/details?id=…"
      />

      <button
        onClick={save}
        disabled={saving}
        data-testid="banner-save-button"
        className="flex items-center justify-center gap-2 rounded-xl bg-emerald-400 px-5 py-2.5 text-sm font-bold text-emerald-950 transition-colors hover:bg-emerald-300 disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer le bandeau"}
      </button>
    </section>
  );
}

function Dashboard({ admin, onLogout }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/images", { headers: authHeader() });
      setImages(data);
    } catch {
      toast.error("Impossible de charger les images");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const uploadFiles = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (list.length === 0) {
      toast.error("Seules les images sont acceptées");
      return;
    }
    setUploading(true);
    let ok = 0;
    for (const file of list) {
      const form = new FormData();
      form.append("file", file);
      try {
        await api.post("/admin/images", form, { headers: authHeader() });
        ok += 1;
      } catch (err) {
        toast.error(`${file.name} : ${err.response?.data?.detail || "échec"}`);
      }
    }
    setUploading(false);
    if (ok > 0) {
      toast.success(`${ok} image(s) uploadée(s)`);
      load();
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const remove = async (img) => {
    if (!window.confirm(`Supprimer « ${img.original_filename} » ?`)) return;
    try {
      await api.delete(`/admin/images/${img.id}`, { headers: authHeader() });
      setImages((prev) => prev.filter((i) => i.id !== img.id));
      toast.success("Image supprimée");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  return (
    <div className="min-h-screen bg-[#070A12] px-4 py-8 sm:px-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
          <div>
            <h1 className="font-outfit text-2xl font-extrabold text-white">Gestion des médias</h1>
            <p className="text-sm text-slate-400">Connecté : {admin.email}</p>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-white/5"
          >
            <LogOut className="h-4 w-4" /> Déconnexion
          </button>
        </header>

        <BannerEditor />

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInput.current?.click()}
          data-testid="admin-upload-zone"
          className={`mt-8 flex cursor-pointer flex-col items-center justify-center rounded-3xl border-2 border-dashed px-6 py-12 text-center transition-colors ${
            dragOver ? "border-emerald-400 bg-emerald-400/5" : "border-white/15 hover:border-emerald-400/50"
          }`}
        >
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            multiple
            className="hidden"
            data-testid="admin-file-input"
            onChange={(e) => {
              uploadFiles(e.target.files);
              e.target.value = "";
            }}
          />
          {uploading ? (
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
          ) : (
            <Upload className="h-8 w-8 text-emerald-400" />
          )}
          <p className="mt-3 font-outfit text-base font-bold text-white">
            {uploading ? "Upload en cours…" : "Glisse tes images ici ou clique pour parcourir"}
          </p>
          <p className="mt-1 text-xs text-slate-500">JPG, PNG, WEBP, GIF · max 10 Mo</p>
        </div>

        <div className="mt-10">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
            <ImageIcon className="h-4 w-4" /> Bibliothèque ({images.length})
          </h2>
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
            </div>
          ) : images.length === 0 ? (
            <p data-testid="admin-empty" className="py-16 text-center text-slate-500">
              Aucune image pour le moment. Upload ta première image ci-dessus.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {images.map((img) => (
                <ImageCard key={img.id} img={img} onDelete={remove} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .get("/auth/me", { headers: authHeader() })
      .then(({ data }) => setAdmin(data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAdmin(null);
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070A12]">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <>
      <Toaster theme="dark" position="top-center" richColors />
      {admin ? (
        <Dashboard admin={admin} onLogout={logout} />
      ) : (
        <LoginView onSuccess={setAdmin} />
      )}
    </>
  );
}
